import React from "react";
import { connect } from "react-redux";
import type { Dispatch } from "redux";
import { withRouter } from "react-router-dom";
import type { RouteComponentProps } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { useListenGivingFormRouter } from "shared/custom-hooks/use-listen-giving-form-router";
import { openModal } from "../../../../ducks/modal";
import {
  pushSelectedCampaign,
  resetCampaignSelections,
  setGivingStep,
  setSingleGive,
  setCampaignDonateAmount,
  setCurrentCampaign,
  setCategory,
  setMatchId,
  setShowCampaignAmountScreen,
} from "../../../../ducks/givingFormV2";
import { closeModal } from "../../../../ducks/matchingGifts";
import MixpanelService from "shared/services/MixpanelService";

export type GivingButtonCampaign = {
  id: string;
  name: string;
  shell_campaign?: boolean;
  auto_dock_category?: string;
  donation_levels?: readonly unknown[];
  default_amount?: number;
  default_giving_day_amount?: number;
};

type SelectedCampaign = {
  id: string;
};

interface GivingButtonRootState {
  givingFormV2: { selectedCampaigns: SelectedCampaign[] };
  entity: { defaultGivingDayAmount: number; entityId: string };
}

type GivingButtonV2OwnProps = {
  campaign: GivingButtonCampaign;
  buttonText?: string;
  cssClasses?: string;
  matchId?: string;
  fromMenuBar?: string;
};

type GivingButtonV2StateProps = {
  entityId: string;
  defaultGivingDayAmount: number;
  selectedCampaigns: SelectedCampaign[];
};

type GivingButtonV2DispatchProps = {
  setCurrentCampaign: (campaign: GivingButtonCampaign) => void;
  setShowCampaignAmountScreen: (value: boolean) => void;
  setCategory: (category: string) => void;
  setMatchId: (id: string) => void;
  closeMatchModal: () => void;
  resetCampaignSelections: () => void;
  setSingleGive: (value: boolean) => void;
  onOpenModal: () => void;
  addCampaign: (campaign: GivingButtonCampaign, entityId: string | undefined) => void;
  setCampaignDonateAmount: (campaignId: string, amount: number) => void;
  setGivingStep: (step: string) => void;
};

type GivingButtonV2Props = GivingButtonV2OwnProps &
  GivingButtonV2StateProps &
  GivingButtonV2DispatchProps &
  RouteComponentProps;

const DEFAULT_BUTTON_CLASSES =
  "m-sm-r giving-nav-giving-btn primary-color-background primary-color-border";

const GivingButtonV2 = ({
  location,
  onOpenModal,
  defaultGivingDayAmount,
  buttonText,
  campaign,
  entityId,
  addCampaign,
  cssClasses,
  resetCampaignSelections,
  selectedCampaigns,
  setGivingStep,
  setSingleGive,
  closeMatchModal,
  setCampaignDonateAmount,
  setCurrentCampaign,
  setShowCampaignAmountScreen,
  setCategory,
  setMatchId,
  matchId,
  fromMenuBar,
}: GivingButtonV2Props) => {
  const classNameProps = cssClasses || DEFAULT_BUTTON_CLASSES;
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  const handleAddCampaign = (campaignToAdd: GivingButtonCampaign) => {
    addCampaign(campaignToAdd, entityId);
    const initialCampaignAmount =
      campaignToAdd.default_amount ??
      campaignToAdd.default_giving_day_amount ??
      defaultGivingDayAmount;

    setCampaignDonateAmount(campaignToAdd.id, initialCampaignAmount);
  };

  const handleSingleGive = () => {
    resetCampaignSelections();
    setSingleGive(true);
    setGivingStep("givingDonationLevels");
    addCampaign(campaign, entityId);
    onOpenModal();
  };

  const handleMultiGive = () => {
    setSingleGive(false);
    if (campaign?.auto_dock_category) {
      setCategory(campaign.auto_dock_category);
    }
    if (campaign && !campaign.shell_campaign) {
      selectedCampaigns.some((selectedCampaign) => selectedCampaign.id === campaign.id)
        ? null
        : handleAddCampaign(campaign);
      setCurrentCampaign(campaign);

      if (isMobile) {
        setShowCampaignAmountScreen(true);
      }
    }
    onOpenModal();
  };

  const startGivingFormLaunch = () => {
    setMatchId(matchId || "");
    // @ts-ignore
    window.SnapAnalytics?.track({
      hitType: "event",
      eventCategory: "giving form",
      eventAction: "donationForm",
    });
    MixpanelService.trackGivingButtonClick(campaign?.name);

    closeMatchModal();
  };

  const clickGive = () => {
    startGivingFormLaunch();
    if (campaign?.donation_levels && campaign.donation_levels.length > 0) {
      handleSingleGive();
    } else {
      handleMultiGive();
    }
  };

  const openGivingFormFromUrl = (autoCategory?: string) => {
    if (!autoCategory) {
      if (fromMenuBar) {
        clickGive();
      }
      return;
    }
    if (fromMenuBar) {
      startGivingFormLaunch();
    }
    setSingleGive(false);
    onOpenModal();
  };

  useListenGivingFormRouter({
    location,
    setCategory,
    onOpenGivingFormFromUrl: openGivingFormFromUrl,
  });

  return (
    <button
      data-auto-id="t-giving-form-v2--GivingB-button"
      aria-label={`Donate to ${campaign?.name ?? "Campaign"}`}
      onClick={clickGive}
      className={classNameProps}>
      {buttonText || "GIVE"}
    </button>
  );
};

const mapStateToProps = ({
  givingFormV2,
  entity,
}: GivingButtonRootState): GivingButtonV2StateProps => {
  const { selectedCampaigns } = givingFormV2;

  const { defaultGivingDayAmount, entityId } = entity;

  return {
    entityId,
    defaultGivingDayAmount,
    selectedCampaigns,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<unknown>): GivingButtonV2DispatchProps => {
  return {
    setCurrentCampaign: (campaign) => dispatch(setCurrentCampaign(campaign)),
    setShowCampaignAmountScreen: (bool) => dispatch(setShowCampaignAmountScreen(bool)),
    setCategory: (category) => dispatch(setCategory(category)),
    setMatchId: (id) => dispatch(setMatchId(id)),
    closeMatchModal: () => dispatch(closeModal()),
    resetCampaignSelections: () => dispatch(resetCampaignSelections()),
    setSingleGive: (isSingleGive) => dispatch(setSingleGive(isSingleGive)),
    onOpenModal: () => dispatch(openModal()),
    addCampaign: (campaign, id) => dispatch(pushSelectedCampaign(campaign, id)),
    setCampaignDonateAmount: (campaignId, amount) =>
      dispatch(setCampaignDonateAmount(campaignId, amount)),
    setGivingStep: (step) => dispatch(setGivingStep(step)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(GivingButtonV2));
